import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, AvatarBadge, StatusBadge, PillTag, ImportWizard } from './ui';
import {
  useC360List, C360Table, ViewSwitch, DEFAULT_VIEW_MODES, C360CardGrid, ColumnChooser, MultiGroupPicker,
  KpiCard, DonutCard, unwrapLookupItems, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon, CardIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/warehouses';
// #59 (2026-07-17 kullanıcı kararı): sistem depoları (TRANSIT) bu listede ARTIK GÖSTERİLMEZ.
// Eskiden /All ucu kullanılıyordu; her distribütör altında salt-okunur bir "TRANSIT / Sistem" satırı
// çıkıyor ve listeyi kirletiyordu. TRANSIT kullanıcıya ait bir tanım değil, transferde malın "yolda"
// beklediği altyapı deposudur (bkz. TransferPostingService / TransitWarehouseProvider) — silinemez ve
// düzenlenmesi gerekmez. Düz uç zaten `!IsSystem` filtresi uygular (WarehousesController.Get).
// NOT: /All ucu backend'de DURUYOR; transit bakiye/fark ekranları TRANSIT'i kendi uçlarından okur.
const LIST_API = API_BASE;

// Depo türü — etiket olarak saklanır (zorunlu). Tek doğruluk kaynağı backend:
// DataScope.Entities/Static/WarehouseTypes.cs
// 'Transit Depo' SİSTEM deposudur (IsSystem=1): kullanıcı oluşturamaz/silemez, formda seçilemez;
// yalnız filtre/liste görünümünde yer alır.
const USER_TYPE_OPTIONS = [
  'Ana Depo', 'Tali Depo', 'Mevzii Depo', 'Emanet Depo', 'Araç Depo', 'İade Deposu', 'Reklam Depo',
];
const TRANSIT_TYPE = 'Transit Depo';
const TYPE_OPTIONS = [...USER_TYPE_OPTIONS, TRANSIT_TYPE];

const STATUS_META = {
  1: { label: 'Aktif', tone: 'ok' },
  0: { label: 'Pasif', tone: 'neutral' },
  2: { label: 'Beklemede', tone: 'wait' },
};
const STATUS_OPTIONS = Object.entries(STATUS_META).map(([value, meta]) => ({ value: Number(value), label: meta.label }));

const GROUP_OPTIONS = [
  { value: 'vendorId', label: 'Distribütör' },
  { value: 'status', label: 'Durum' },
];

const GROUP_BY_ODATA = {
  vendorId: { field: 'VendorId', type: 'guid' },
  status: { field: 'Status', type: 'number' },
};

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  name: { type: 'text', odataField: 'Name' },
  vendorLabel: { type: 'select', odataField: 'VendorId', valueFormat: 'guid' },
  type: { type: 'select', odataField: 'Type', options: TYPE_OPTIONS.map((t) => ({ value: t, label: t })) },
  status: { type: 'select', odataField: 'Status', options: STATUS_OPTIONS },
  erpId: { type: 'text', odataField: 'ErpId' },
};

const SEARCH_FIELDS = ['TextId', 'Name', 'ErpId', 'Description', 'Type'];

// Liste/Kanban/Grafik + Kart görünümü. Kart, Liste'nin hemen YANINDA (index 1); geri kalan modlar korunur.
const WAREHOUSE_VIEW_MODES = (() => {
  const list = DEFAULT_VIEW_MODES.find((m) => m.value === 'list');
  const rest = DEFAULT_VIEW_MODES.filter((m) => m.value !== 'list');
  const cardMode = { value: 'card', label: 'Kart', icon: CardIcon };
  return list ? [list, cardMode, ...rest] : [cardMode, ...DEFAULT_VIEW_MODES];
})();

// === Depo Grafik görünümü ===
function ChartView({ rows, getVendorLabel }) {
  const total = rows.length;
  const active = rows.filter((r) => Number(r.status) === 1).length;
  const vendorSet = new Set(rows.map((r) => r.vendorId).filter(Boolean));
  const now = new Date();
  const ymCurrent = now.getFullYear() * 100 + (now.getMonth() + 1);
  const newThisMonth = rows.filter((r) => {
    if (!r.createdDate) return false;
    const d = new Date(r.createdDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getFullYear() * 100 + (d.getMonth() + 1) === ymCurrent;
  }).length;

  const vendorCounts = {};
  rows.forEach((r) => {
    const lbl = r.vendorId ? getVendorLabel(r.vendorId) : 'Atanmamış';
    vendorCounts[lbl] = (vendorCounts[lbl] || 0) + 1;
  });
  const vendorData = Object.entries(vendorCounts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 12);

  const statusPalette = { ok: '#00c875', neutral: '#94a3b8', wait: '#fdab3d' };
  const statusCounts = {};
  rows.forEach((r) => {
    const k = r.status == null || r.status === '' ? '__none__' : Number(r.status);
    statusCounts[k] = (statusCounts[k] || 0) + 1;
  });
  const statusData = Object.entries(statusCounts).map(([k, v]) => {
    const meta = STATUS_META[k];
    return { name: meta?.label || 'Bilinmiyor', value: v, color: meta ? statusPalette[meta.tone] : '#cbd5e1' };
  });

  return (
    <div className="c360-dashboard">
      <div className="c360-kpi-grid">
        <KpiCard label="Toplam Depo" value={total.toLocaleString('tr-TR')} />
        <KpiCard label="Aktif Depo" value={active.toLocaleString('tr-TR')} hint={total > 0 ? `%${((active / total) * 100).toFixed(0)} aktif` : '—'} />
        <KpiCard label="Distribütör" value={vendorSet.size.toLocaleString('tr-TR')} hint="farklı distribütör" />
        <KpiCard label="Bu Ay Eklenen" value={newThisMonth.toLocaleString('tr-TR')} hint="Yeni depo" />
      </div>

      <div className="c360-chart-panel">
        <div className="c360-chart-panel-head">
          <span className="c360-chart-panel-title">Distribütörye Göre Dağılım</span>
          <span className="c360-chart-panel-sub">İlk {vendorData.length} distribütör</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={vendorData} margin={{ top: 12, right: 8, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" fontSize={11} angle={-25} textAnchor="end" interval={0} height={60} />
            <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
            <Tooltip cursor={{ fill: 'rgba(14, 165, 233, 0.06)' }} formatter={(v) => [`${v} depo`, 'Adet']} />
            <Bar dataKey="value" fill="#0ea5e9" radius={[8, 8, 0, 0]}>
              <LabelList dataKey="value" position="top" style={{ fill: '#334155', fontWeight: 800, fontSize: 11 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="c360-chart-donuts">
        <DonutCard title="Durum Dağılımı" subtitle={`${total} depo`} data={statusData} />
      </div>
    </div>
  );
}

export function WarehousesPage() {
  usePageMeta('Depolar', 'Depo kayıtlarını yönetin');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [vendors, setVendors] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  // #769.4 Depo kullanımdaysa backend'in sorduğu "pasife alınsın mı?" metni (null = sorulmuyor).
  const [passivateAsk, setPassivateAsk] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const getVendorLabel = useCallback((id) => {
    const v = vendors.find((item) => item.id === id);
    if (!v) return '-';
    return v.name ? `${v.textId || ''} - ${v.name}`.trim().replace(/^- /, '') : v.textId || '-';
  }, [vendors]);

  useEffect(() => {
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((res) => res.ok ? res.json() : [])
      .then((data) => setVendors(unwrapLookupItems(data)))
      .catch(() => {})
      .finally(() => setLookupsLoaded(true));
  }, []);

  const getKeyLabel = useCallback((field, row) => {
    let rawKey = row[field];
    if (rawKey === undefined || rawKey === null || rawKey === '') rawKey = '__none__';
    const key = (field === 'status') ? (rawKey === '__none__' ? '__none__' : Number(rawKey)) : rawKey;
    let label;
    if (key === '__none__') label = 'Atanmamış';
    else if (field === 'status') label = STATUS_META[key]?.label || String(rawKey);
    else if (field === 'vendorId') label = getVendorLabel(key);
    else label = String(key);
    return { key, label };
  }, [getVendorLabel]);

  const seedKanbanBuckets = useCallback((groupField) => {
    if (groupField === 'status') return Object.entries(STATUS_META).map(([k, meta]) => ({ key: Number(k), label: meta.label }));
    if (groupField === 'vendorId') return vendors.map((v) => ({ key: v.id, label: getVendorLabel(v.id) }));
    return [];
  }, [vendors, getVendorLabel]);

  // $select: yalnız görünür kolonların OData alanları + satır anahtarı/silme-diyaloğu ad'ı.
  // sortField/needsFullData hook return'ünden geldiği için burada verilmez; hook grup/kanban/chart
  // görünümünde $select'i zaten uygulamaz (tam veri), sıralama alanı $select dışı da çalışır.
  // IsSystem always'te: sistem deposu (TRANSIT) rozeti + Düzenle/Sil gizleme satırdan okunur.
  const selectFields = useMemo(
    () => buildSelectFields({ columnFilterMeta: COLUMN_FILTER_META, columnVisibility, always: ['Id', 'Name', 'IsSystem'] }),
    [columnVisibility],
  );

  const list = useC360List({
    apiBase: LIST_API,
    columnFilterMeta: COLUMN_FILTER_META,
    groupByOdata: GROUP_BY_ODATA,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    defaultKanbanGroup: 'status',
    getKeyLabel,
    seedKanbanBuckets,
    ready: lookupsLoaded,
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading, loadingMore,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    viewMode, setViewMode, groupBy, setGroupBy, isGrouping,
    expandedGroups, toggleGroupExpand, bucketCounts, bucketLoadingByPath,
    groupBuckets, nestedGroupBuckets, fetchData, loadMore, exportRows,
  } = list;

  const openNew = () => navigate('/warehouses/new');
  const openDetail = (row = selected) => { if (!row) return; navigate(`/warehouses/${row.id}`, { state: { row } }); };
  // #282 kalem → düzenleme modu (DetailShell ?edit=1 ile açılır).
  const openEditRow = (row = selected) => { if (!row) return; navigate(`/warehouses/${row.id}?edit=1`, { state: { row } }); };

  /**
   * #769.4 (2. tur) İKİ AŞAMALI SİLME — WarehouseDetailPage ile aynı sözleşme.
   *
   * Onaysız ilk çağrıda backend, depo kullanımdaysa HİÇBİR ŞEY YAZMADAN `needsPassivateConfirm`
   * döner; pasife alma kararını kullanıcı verir. Eskiden sormadan pasife alınıyor ve sonuç kırmızı
   * hata tonuyla bildiriliyordu ("uyarıya evet sil dediğimde yine aynı hatayı veriyor").
   */
  const handleDelete = async (confirmPassivate = false) => {
    if (!selected) return;
    try {
      const qs = confirmPassivate ? '?confirmPassivate=true' : '';
      const res = await fetch(`${API_BASE}/Delete${qs}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const body = await res.json().catch(() => null);
      // Backend'in mesajını GÖSTER — kullanıcı neden silinemediğini öğrenebilmeli.
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');

      const data = body?.data ?? body?.Data;
      if (data && (data.needsPassivateConfirm ?? data.NeedsPassivateConfirm)) {
        setPassivateAsk(body?.message || body?.Message
          || 'Bu depo kullanımda olduğu için silinemez. Pasife alınsın mı?');
        setDeleteConfirm(false);
        return;
      }

      if (data && (data.passivated ?? data.Passivated)) {
        showToast('info', body?.message || body?.Message || 'Depo silinmedi, pasife alındı.');
        setPassivateAsk(null);
        setDeleteConfirm(false);
        setSelected(null);
        fetchData();
        return;
      }

      showToast('success', 'Depo başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
      setPassivateAsk(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Depo Kodu', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', {
      header: 'Depo Adı', meta: { filterType: 'text' },
      cell: (info) => {
        const v = info.getValue();
        if (!v) return '-';
        return (
          <span className="c360-customer-cell">
            <AvatarBadge name={v} tone="customer" />
            <span className="c360-cell-main">{v}</span>
            {info.row.original.isSystem && (
              <span title="Sistem deposu — düzenlenemez / silinemez"><PillTag tone="danger">Sistem</PillTag></span>
            )}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => getVendorLabel(row.vendorId), {
      id: 'vendorLabel', header: 'Distribütör', enableSorting: false, meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return (!v || v === '-') ? '-' : <PillTag tone="info">{v}</PillTag>; },
    }),
    columnHelper.accessor('type', {
      header: 'Tür', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return v ? <PillTag tone="info">{v}</PillTag> : '-'; },
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => {
        const v = info.getValue();
        if (v === null || v === undefined || v === '') return '-';
        const meta = STATUS_META[v] ?? { label: String(v), tone: 'neutral' };
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    }),
    columnHelper.accessor('erpId', { header: 'ERP Kodu', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      // Sistem deposu (TRANSIT, IsSystem=1): backend zaten silmeyi reddeder ve tür/kod değişimini
      // yok sayar → UI'da Düzenle/Sil hiç gösterilmez (yalnız Detay).
      cell: ({ row }) => {
        const isSystem = !!row.original.isSystem;
        return (
          <span className="c360-actions">
            <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Detay" aria-label="Detay" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
            {canEdit && !isSystem && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Düzenle" aria-label="Düzenle" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
            {canDelete && !isSystem && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
          </span>
        );
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [getVendorLabel, canEdit, canDelete]);

  const lookupOptions = useMemo(() => ({
    vendorLabel: vendors.map((v) => ({ value: v.id, label: v.name ? `${v.textId || ''} - ${v.name}`.replace(/^- /, '') : (v.textId || v.id) })),
  }), [vendors]);

  const resolveFilterProps = useCallback((columnId, meta) => ({ options: meta.options || lookupOptions[columnId] }), [lookupOptions]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Depo Kodu' },
        { field: 'name', headerName: 'Depo Adı' },
        { headerName: 'Distribütör', valueGetter: ({ row }) => getVendorLabel(row.vendorId) },
        { field: 'type', headerName: 'Tür' },
        { headerName: 'Durum', valueGetter: ({ row }) => (STATUS_META[row.status]?.label ?? row.status ?? '') },
        { field: 'erpId', headerName: 'ERP Kodu' },
        { field: 'adress', headerName: 'Adres' },
        { field: 'description', headerName: 'Açıklama' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `depolar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma === (Distribütör, kod ile eşlenir)
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const importRowToPayload = (row) => {
    const vendorCode = importPick(row, ['Distribütör Kodu', 'Distributor Kodu', 'Tedarikçi Kodu', 'Tedarikci Kodu', 'Tedarikçi', 'Distribütör', 'vendorTextId']);
    const vendor = vendors.find((v) => (v.textId || '').toLowerCase() === vendorCode.toLowerCase());
    return {
      textId: importPick(row, ['Depo Kodu', 'Kod', 'textId', 'TextId']),
      name: importPick(row, ['Depo Adı', 'Depo Adi', 'Ad', 'name', 'Name']),
      erpId: importPick(row, ['ERP Kodu', 'erpId']),
      type: importPick(row, ['Tür', 'Tur', 'type']),
      adress: importPick(row, ['Adres', 'adress']),
      description: importPick(row, ['Açıklama', 'Aciklama', 'description']),
      vendorId: vendor?.id || null,
      _vendorCode: vendorCode,
    };
  };
  const importValidateRow = (p) => {
    if (!p.textId) return 'Depo Kodu boş';
    if (!p.name) return 'Depo Adı boş';
    if (!p.vendorId) return p._vendorCode ? `Distribütör bulunamadı: ${p._vendorCode}` : 'Distribütör Kodu boş';
    // Transit deposu SİSTEM tarafından üretilir (her Vendor için tekil) → içe aktarımla oluşturulamaz.
    if (p.type && p.type.toLocaleLowerCase('tr') === TRANSIT_TYPE.toLocaleLowerCase('tr')) return 'Transit Depo sistem tarafından oluşturulur, içe aktarılamaz';
    if (p.type && !USER_TYPE_OPTIONS.includes(p.type)) return `Geçersiz tür: ${p.type}`;
    return null;
  };
  const importSubmitRow = async (p) => {
    const payload = { textId: p.textId, name: p.name, adress: p.adress || null, description: p.description || null, erpId: p.erpId || null, type: p.type || null, status: 1, vendorId: p.vendorId };
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Depo Kodu': 'D001', 'Depo Adı': 'Örnek Depo', 'Distribütör Kodu': 'V001', 'Tür': 'Ana Depo', 'ERP Kodu': '', 'Adres': '', 'Açıklama': '' }], null, 'depo_sablon');
  };

  const handleKanbanColScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMore();
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, ad, ERP veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      {viewMode !== 'chart' && <MultiGroupPicker value={groupBy} onChange={setGroupBy} options={GROUP_OPTIONS} />}
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Depolar"
        subtitle="Depo kayıtlarını filtreleyin, sıralayın ve yönetin."
        actions={(
          <>
            <ViewSwitch value={viewMode} onChange={setViewMode} modes={WAREHOUSE_VIEW_MODES} />
            {canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Ekle</button>}
          </>
        )}
        toolbar={toolbarNode}
        footer={(viewMode === 'kanban' || viewMode === 'chart' || isGrouping) ? null : footerNode}
      >
        {viewMode === 'chart' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <ChartView rows={rows} getVendorLabel={getVendorLabel} />
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'kanban' ? (
          <div className="c360-table-wrap" style={{ position: 'relative' }}>
            <div className="c360-kanban">
              {groupBuckets.map((bucket) => (
                <div key={String(bucket.key)} className="c360-kanban-col">
                  <div className="c360-kanban-col-head">
                    <span className="c360-kanban-col-title">{bucket.label}</span>
                    <span className="c360-kanban-col-count">{bucketCounts[String(bucket.key)] != null ? bucketCounts[String(bucket.key)] : bucket.rows.length}</span>
                  </div>
                  <div className="c360-kanban-col-body" onScroll={handleKanbanColScroll}>
                    {bucket.rows.length === 0 ? (
                      <div className="c360-kanban-empty">Kayıt yok</div>
                    ) : (
                      bucket.rows.map((row) => {
                        const rowStatusMeta = STATUS_META[row.status];
                        const isV = (id) => columnVisibility[id] !== false;
                        const vendorLbl = getVendorLabel(row.vendorId);
                        return (
                          <div key={row.id} className="c360-kanban-card" onClick={() => setSelected(row)} onDoubleClick={() => openDetail(row)}>
                            {isV('name') && (
                              <div className="c360-kanban-card-head">
                                {row.name && <AvatarBadge name={row.name} tone="customer" size={28} />}
                                <span className="c360-kanban-card-title" title={row.name || ''}>{row.name || '-'}</span>
                              </div>
                            )}
                            <div className="c360-kanban-card-meta">
                              {isV('textId') && row.textId && <span className="c360-erp-chip">{row.textId}</span>}
                              {row.isSystem && <PillTag tone="danger">Sistem</PillTag>}
                              {isV('status') && rowStatusMeta && <StatusBadge tone={rowStatusMeta.tone}>{rowStatusMeta.label}</StatusBadge>}
                              {isV('vendorLabel') && vendorLbl && vendorLbl !== '-' && <span className="c360-erp-chip" title="Distribütör">{vendorLbl}</span>}
                            </div>
                            {isV('erpId') && row.erpId && <div className="c360-kanban-card-sub">{row.erpId}</div>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalCount > 0 && (
              <div className="c360-kanban-loadbar">
                {loadingMore
                  ? `⏳ Yükleniyor… (${rows.length} / ${totalCount})`
                  : rows.length >= totalCount
                    ? `✓ Tümü yüklendi · ${totalCount} kayıt`
                    : `${rows.length} / ${totalCount} kayıt yüklü · Devamı için bir kolonun içinde aşağı kaydırın`}
              </div>
            )}
            {loading && <div style={s.loadingOverlay}>Yükleniyor...</div>}
          </div>
        ) : viewMode === 'card' ? (
          <C360CardGrid
            rows={table.getRowModel().rows.map((r) => r.original)}
            card={(row) => {
              const meta = STATUS_META[row.status];
              const vendorLbl = getVendorLabel(row.vendorId);
              return {
                avatarName: row.name,
                avatarTone: 'customer',
                title: row.name,
                subtitle: row.textId,
                statusTone: meta?.tone,
                statusLabel: meta?.label,
                meta: [
                  vendorLbl && vendorLbl !== '-' ? { k: 'Distribütör', v: vendorLbl } : null,
                  row.type ? { k: 'Tür', v: row.type } : null,
                  row.isSystem ? { k: 'Sistem', v: 'Evet' } : null,
                ].filter(Boolean),
              };
            }}
            actions={(row) => {
              const isSystem = !!row.isSystem;
              return [
                { kind: 'view', title: 'Detay', onClick: openDetail },
                { kind: 'edit', title: 'Düzenle', onClick: openEditRow, hidden: !canEdit || isSystem },
                { kind: 'delete', title: 'Sil', onClick: (r) => { setSelected(r); setDeleteConfirm(true); }, hidden: !canDelete || isSystem },
              ];
            }}
            onCardClick={setSelected}
            onCardDoubleClick={openDetail}
            selectedId={selected?.id}
            loading={loading}
          />
        ) : (
          <C360Table
            table={table}
            columnFilterMeta={COLUMN_FILTER_META}
            columnFilters={columnFilters}
            onApplyFilter={setColumnFilter}
            onClearFilter={clearColumnFilter}
            resolveFilterProps={resolveFilterProps}
            isGrouping={isGrouping}
            nestedGroupBuckets={nestedGroupBuckets}
            expandedGroups={expandedGroups}
            toggleGroupExpand={toggleGroupExpand}
            bucketCounts={bucketCounts}
            bucketLoadingByPath={bucketLoadingByPath}
            loading={loading}
            selected={selected}
            onRowClick={setSelected}
            onRowDoubleClick={openDetail}
            emptyText="Kayıt bulunamadı"
          />
        )}
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{selected?.name}"</strong> deposunu silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={() => handleDelete(false)}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* #769.4 İkinci aşama: depo kullanımda. Pasife alma kararı kullanıcınındır. */}
      {passivateAsk && (
        <div style={s.overlay} onClick={() => setPassivateAsk(null)}>
          <div style={{ ...s.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={s.modalTitle}>Depo Kullanımda</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>{passivateAsk}</p>
              <p style={{ margin: '10px 0 0', color: 'var(--text-secondary)', lineHeight: 1.6, fontSize: 13 }}>
                Pasife alınan depo listede kalır ve geçmiş hareketleri korunur; yeni belgelerde
                seçilemez. Vazgeçerseniz hiçbir şey değişmez.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} onClick={() => handleDelete(true)}>Evet, Pasife Al</button>
              <button style={s.btnSecondary} onClick={() => setPassivateAsk(null)}>Vazgeç</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} depo içe aktarıldı`); fetchData(); } }}
        title="Depo İçe Aktar"
        helpText="Zorunlu alanlar: Depo Kodu, Depo Adı ve Distribütör Kodu (mevcut bir distribütör koduyla eşleşmeli)."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
